import axios from 'axios'
import { ref } from 'vue'
import { useAuthStore } from '@/stores/auth'

// Create a global router reference
let router

// Fix: create a method to initialize the router after Vue app is created
export function initializeAxios(appRouter) {
  router = appRouter
}

// Create axios instance with common configuration
const api = axios.create({
  baseURL: import.meta.env.TMKT_API_URL,
  timeout: 61_000, // Request timeout in milliseconds
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for API calls
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// Response interceptor with better error handling
api.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    const statusCode = error.response ? error.response.status : null

    // Handle 401 Unauthorized errors globally
    if (statusCode === 401) {
      // Clear auth data
      localStorage.removeItem('access_token')
      localStorage.removeItem('token')
      localStorage.removeItem('user')

      // Only redirect to login if not already on the login page
      // and if we're not trying to login (to avoid redirect loops)
      const currentPath = window.location.pathname
      if (!currentPath.includes('/login') && router) {
        // Use the globally available router instance
        router.push({
          path: '/login',
          query: { reason: 'session_expired' },
        })
      }
    }

    return Promise.reject(error)
  },
)

// Composition API compatible helper functions
export function useApi(initialEndpoint) {
  let endpoint = initialEndpoint

  const data = ref(null)
  const loading = ref(false)
  const error = ref(null)

  const fetch = async (params = {}) => {
    loading.value = true
    error.value = null

    const originalEndpoint = endpoint

    try {
      if (params.id != undefined && endpoint != '/pedidos') {
        endpoint += '/' + params.id
        delete params.id
      }
      const response = await api.get(endpoint, params)
      data.value = response.data
      return response.data
    } catch (err) {
      error.value = err?.response?.data?.message || err.message || 'Erro ao carregar dados'
      throw err
    } finally {
      endpoint = originalEndpoint
      loading.value = false
    }
  }

  const post = async (payload) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.post(endpoint, payload)
      return response.data
    } catch (err) {
      error.value = err.response ? err.response.data : err.message || 'Erro ao enviar dados'
      throw err
    } finally {
      loading.value = false
    }
  }

  const postWithId = async (id, payload) => {
    loading.value = true
    error.value = null

    try {
      const response = await api.post(`${endpoint}/${id}`, payload)
      return response.data
    } catch (err) {
      error.value = err.response ? err.response.data : err.message || 'Erro ao enviar dados'
      throw err
    } finally {
      loading.value = false
    }
  }

  const put = async (payload, id = '') => {
    const originalEndpoint = endpoint
    loading.value = true
    error.value = null

    try {
      if (id != '') {
        endpoint += '/' + id
      }
      const response = await api.put(endpoint, payload)
      return response.data
    } catch (err) {
      error.value = err.response ? err.response.data : err.message || 'Erro ao atualizar dados'
      throw err
    } finally {
      endpoint = originalEndpoint
      loading.value = false
    }
  }

  const del = async (id, payload = {}) => {
    const originalEndpoint = endpoint
    loading.value = true
    error.value = null
    try {
      if (id !== '' && id !== undefined) {
        endpoint += '/' + id
      }
      const response = await api.delete(`${endpoint}`, { data: payload })
      return response.data
    } catch (err) {
      error.value = err.response ? err.response.data : err.message || 'Erro ao excluir dados'
      throw err
    } finally {
      endpoint = originalEndpoint
      loading.value = false
    }
  }

  return {
    data,
    loading,
    error,
    fetch,
    post,
    postWithId,
    put,
    del,
  }
}

export default api
